import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, InlineNotification, Modal, NumberInput, Tag, Toggle } from '@carbon/react';
import { formatVnd, GAME_BY_ID } from '@ruby-cms/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { Money, When } from '../components/format';
import { CommandStatus } from '../components/labels';
import type { Command } from './Commands';
import { BotHuFields, clamp, num, pct, secs, type BotGop, type BotNo } from './JackpotBot';
import { MoneyInput } from '../components/MoneyInput';

type Table = {
  gid: string; name: string; bat: boolean; soDuQuy: number | null; overridden: boolean;
  quy: { tyLeVaoQuy: number; quyToiDa: number; traToiDaMoiVan: number };
  bot: { soBot: number; soBotChat: number; cuoc: { bat: boolean; tyLeVaoVan: number } };
};
type BotGame = { gid: string; name: string; overridden: boolean; bot: { bat: boolean; soBotChat: number; cuoc: { bat: boolean; heSoDem: number } } };
type Global = {
  overridden: boolean;
  hu: {
    nguoiThatNoTuNhien: boolean; botNo: BotNo;
    // absent on a he A build older than the bot growth control
    botGop?: BotGop;
  };
  thongBao: { bat: boolean; noHu: { bat: boolean }; thangLon: { bat: boolean; giayMin: number; giayMax: number; tienMin: number; tienMax: number } };
  bot: { choiAo: { bat: boolean; giay: number } };
};
type Pending = { id: string; status: string; createdAt: string; reason: string; sourceType: string | null; sourceId: string | null };
type Data = { tables: Table[]; games: BotGame[]; global: Global | null; meta: { version: number; source: string; at: string | null }; pending: Pending[] };
type Form = { bat: boolean; vaoQuy: number; quyToiDa: string; traToiDa: string; soBot: number; soBotChat: number; botCuoc: boolean; botVaoVan: number };

const toForm = (t: Table): Form => ({
  bat: t.bat, vaoQuy: pct(t.quy.tyLeVaoQuy), quyToiDa: t.quy.quyToiDa ? String(t.quy.quyToiDa) : '', traToiDa: t.quy.traToiDaMoiVan ? String(t.quy.traToiDaMoiVan) : '',
  soBot: t.bot.soBot, soBotChat: t.bot.soBotChat, botCuoc: t.bot.cuoc.bat, botVaoVan: pct(t.bot.cuoc.tyLeVaoVan),
});
// the pool switch (bat) and the pool share (tyLeVaoQuy, task quy-vao-98-cms) are the founder's alone (founder-only.ts): another
// admin's save leaves them out, so the saved values stay; the founder's save sends the share only when changed on this form (a
// bot-only save never rounds or rewinds a share set on MKT và quỹ)
const toBlock = (f: Form, founder: boolean, goc: Form) => ({
  ...(founder ? { bat: f.bat } : {}),
  quy: {
    ...(founder && f.vaoQuy !== goc.vaoQuy ? { tyLeVaoQuy: f.vaoQuy / 100 } : {}),
    quyToiDa: Number(f.quyToiDa || 0), traToiDaMoiVan: Number(f.traToiDa || 0),
  },
  bot: { soBot: f.soBot, soBotChat: f.soBotChat, cuoc: { bat: f.botCuoc, tyLeVaoVan: f.botVaoVan / 100 } },
});

/** Reset / create buttons and the approval modal every panel shares. */
function SaveBar({ editable, dirty, invalid, heading, warning, reset, body }: {
  editable: boolean; dirty: boolean; invalid?: string | null; heading: string; warning: string; reset: () => void; body: () => unknown;
}) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const m = useMutation({
    mutationFn: () => api.put<{ command: Command }>('/api/admin/games/control', body()),
    onSuccess: (r) => nav('/commands/' + r.command.id),
  });
  if (!editable) return null;
  return (
    <>
      {invalid ? <p className="num--neg note">{invalid}</p> : null}
      <div className="row control__actions">
        <Button kind="ghost" size="sm" disabled={!dirty} onClick={reset}>Bỏ thay đổi</Button>
        <Button kind="primary" size="sm" disabled={!dirty || !!invalid} onClick={() => setOpen(true)}>Lưu, áp dụng ngay</Button>
      </div>
      <Modal open={open} modalHeading={heading} primaryButtonText="Áp dụng ngay" secondaryButtonText="Hủy"
        primaryButtonDisabled={m.isPending} onRequestClose={() => setOpen(false)} onRequestSubmit={() => m.mutate()}>
        <div className="stack">
          <p>{warning} Máy chủ áp dụng ngay, không chờ người duyệt.</p>
          <InlineError error={m.error} />
        </div>
      </Modal>
    </>
  );
}

function Source({ overridden }: { overridden: boolean }) {
  return overridden ? <Tag size="sm" type="blue">CMS đặt</Tag> : <Tag size="sm" type="gray">mặc định</Tag>;
}

function TablePanel({ t, version, editable, founder }: { t: Table; version: number; editable: boolean; founder: boolean }) {
  const [f, setF] = useState<Form>(() => toForm(t));
  const dirty = JSON.stringify(f) !== JSON.stringify(toForm(t));
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((o) => ({ ...o, [k]: v }));
  return (
    <section className="panel control">
      <div className="panel__head">
        <h2>{t.name} <span className="muted num">{t.gid}</span></h2>
        <Source overridden={t.overridden} />
      </div>
      <div className="control__pool">
        <span className="kpi__label">Số dư quỹ</span>
        <span className="kpi__value">{t.soDuQuy == null ? <span className="num--neg">không đọc được</span> : <Money v={t.soDuQuy} />}</span>
      </div>
      <fieldset className="control__group" disabled={!editable}>
        <legend>Quỹ</legend>
        <Toggle id={'bat-' + t.gid} size="sm" labelText="Điều khiển kết quả qua quỹ" labelA="Tắt, kết quả ngẫu nhiên" labelB="Bật" toggled={f.bat}
          disabled={!founder} onToggle={(v) => set('bat', v)} />
        {founder ? null : <p className="muted note">Chỉ chủ sở hữu bật hoặc tắt quỹ chặn (trang MKT và quỹ).</p>}
        <NumberInput id={'vq-' + t.gid} size="sm" label="Phần cược người thật vào quỹ (%)" min={0} max={100} step={1} value={f.vaoQuy}
          disabled={!founder} onChange={(_e, { value }) => set('vaoQuy', clamp(value, 0, 100))} />
        {founder ? null : <p className="muted note">Chỉ chủ sở hữu đổi phần cược vào quỹ (trang MKT và quỹ).</p>}
        <MoneyInput id={'qt-' + t.gid} size="sm" labelText="Trần quỹ (để trống = không trần)" maxDigits={13} value={f.quyToiDa || ''}
          onChange={(v) => set('quyToiDa', v)} />
        <MoneyInput id={'tt-' + t.gid} size="sm" labelText="Trả người thật tối đa mỗi ván (để trống = chỉ giới hạn bởi quỹ)" maxDigits={13}
          value={f.traToiDa || ''} onChange={(v) => set('traToiDa', v)} />
      </fieldset>
      <fieldset className="control__group" disabled={!editable}>
        <legend>Bot</legend>
        <div className="row">
          <NumberInput id={'sb-' + t.gid} size="sm" label="Số bot" min={0} max={2000} step={10} value={f.soBot} onChange={(_e, { value }) => set('soBot', Math.floor(num(value)))} />
          <NumberInput id={'sc-' + t.gid} size="sm" label="Số bot chat" min={0} max={2000} step={10} value={f.soBotChat} onChange={(_e, { value }) => set('soBotChat', Math.floor(num(value)))} />
        </div>
        <Toggle id={'bc-' + t.gid} size="sm" labelText="Bot đặt cược" labelA="Tắt" labelB="Bật" toggled={f.botCuoc} onToggle={(v) => set('botCuoc', v)} />
        <NumberInput id={'bv-' + t.gid} size="sm" label="Tỷ lệ bot vào mỗi ván (%)" min={0} max={100} step={5} value={f.botVaoVan}
          onChange={(_e, { value }) => set('botVaoVan', clamp(value, 0, 100))} />
        <p className="muted note">Số bot và số bot chat có hiệu lực khi hệ A khởi động lại; các mục khác áp dụng từ ván sau.</p>
      </fieldset>
      <SaveBar editable={editable} dirty={dirty} heading={`Đổi điều khiển ${t.name}`} warning="Thay đổi này đổi số tiền trả cho người chơi."
        reset={() => setF(toForm(t))} body={() => ({ scope: 'table', gid: t.gid, block: toBlock(f, founder, toForm(t)), expect: version })} />
    </section>
  );
}

function GamePanel({ g, version, editable }: { g: BotGame; version: number; editable: boolean }) {
  const [b, setB] = useState(g.bot);
  const dirty = JSON.stringify(b) !== JSON.stringify(g.bot);
  const id = (k: string) => k + '-' + g.gid;
  return (
    <section className="panel control">
      <div className="panel__head">
        <h2>{g.name} <span className="muted num">{g.gid}</span></h2>
        <Source overridden={g.overridden} />
      </div>
      <fieldset className="control__group" disabled={!editable}>
        <legend>Bot</legend>
        <Toggle id={id('gb')} size="sm" labelText="Bot của game" labelA="Tắt hết bot (cược và chat)" labelB="Bật" toggled={b.bat} onToggle={(v) => setB((o) => ({ ...o, bat: v }))} />
        <NumberInput id={id('gc')} size="sm" label="Số bot chat" min={0} max={2000} step={10} value={b.soBotChat}
          onChange={(_e, { value }) => setB((o) => ({ ...o, soBotChat: Math.floor(clamp(value, 0, 2000)) }))} />
        <Toggle id={id('gcu')} size="sm" labelText="Bot đặt cược" labelA="Tắt" labelB="Bật" toggled={b.cuoc.bat} onToggle={(v) => setB((o) => ({ ...o, cuoc: { ...o.cuoc, bat: v } }))} />
        <NumberInput id={id('gd')} size="sm" label="Ban đêm bot cược thưa hơn (nhân nhịp, 1 = như ban ngày)" min={0} max={20} step={0.5} value={b.cuoc.heSoDem}
          onChange={(_e, { value }) => setB((o) => ({ ...o, cuoc: { ...o.cuoc, heSoDem: clamp(value, 0, 20) } }))} />
        <p className="muted note">Bot chỉ tạo không khí: tiền bot là số ảo, không vào ví, sổ giao dịch hay quỹ. Áp dụng từ nhịp bot kế tiếp.</p>
      </fieldset>
      <SaveBar editable={editable} dirty={dirty} heading={`Đổi bot ${g.name}`} warning="Thay đổi này đổi số bot và nhịp bot của game."
        reset={() => setB(g.bot)} body={() => ({ scope: 'game', gid: g.gid, block: { bot: b }, expect: version })} />
    </section>
  );
}

type GlobalForm = Omit<Global, 'overridden'>;
const globalForm = (x: Global): GlobalForm => ({ hu: x.hu, thongBao: x.thongBao, bot: x.bot });

function GlobalPanel({ x, version, editable }: { x: Global; version: number; editable: boolean }) {
  const [f, setF] = useState<GlobalForm>(() => globalForm(x));
  const dirty = JSON.stringify(f) !== JSON.stringify(globalForm(x));
  const hu = (p: Partial<BotNo>) => setF((o) => ({ ...o, hu: { ...o.hu, botNo: { ...o.hu.botNo, ...p } } }));
  const gop = (p: Partial<BotGop>) => setF((o) => (o.hu.botGop ? { ...o, hu: { ...o.hu, botGop: { ...o.hu.botGop, ...p } } } : o));
  const tl = (p: Partial<GlobalForm['thongBao']['thangLon']>) => setF((o) => ({ ...o, thongBao: { ...o.thongBao, thangLon: { ...o.thongBao.thangLon, ...p } } }));
  const t = f.thongBao.thangLon;
  const invalid = t.giayMin > t.giayMax ? 'Thắng lớn ảo: số giây tối thiểu lớn hơn tối đa.'
    : t.tienMin > t.tienMax ? 'Thắng lớn ảo: số tiền tối thiểu lớn hơn tối đa.' : null;
  return (
    <section className="panel control">
      <div className="panel__head">
        <h2>Luật hũ và thông báo</h2>
        <Source overridden={x.overridden} />
      </div>
      <fieldset className="control__group" disabled={!editable}>
        <legend>Nổ hũ (mọi game)</legend>
        <Toggle id="g-nt" size="sm" labelText="Người thật tự nổ hũ"
          labelA="Tắt: chỉ bot nổ hũ; người thật chỉ nổ khi founder chỉ định" labelB="Bật: người thật nổ hũ theo luật game"
          toggled={f.hu.nguoiThatNoTuNhien} onToggle={(v) => setF((o) => ({ ...o, hu: { ...o.hu, nguoiThatNoTuNhien: v } }))} />
        <BotHuFields id="g" botNo={f.hu.botNo} botGop={f.hu.botGop} onBotNo={hu} onBotGop={gop} />
      </fieldset>
      <fieldset className="control__group" disabled={!editable}>
        <legend>Chữ chạy sảnh</legend>
        <Toggle id="g-tb" size="sm" labelText="Chữ chạy sảnh" labelA="Tắt" labelB="Bật" toggled={f.thongBao.bat}
          onToggle={(v) => setF((o) => ({ ...o, thongBao: { ...o.thongBao, bat: v } }))} />
        <Toggle id="g-tbh" size="sm" labelText="Báo nổ hũ" labelA="Tắt" labelB="Bật" toggled={f.thongBao.noHu.bat}
          onToggle={(v) => setF((o) => ({ ...o, thongBao: { ...o.thongBao, noHu: { bat: v } } }))} />
        <Toggle id="g-tbt" size="sm" labelText="Báo thắng lớn ảo của bot" labelA="Tắt" labelB="Bật" toggled={t.bat} onToggle={(v) => tl({ bat: v })} />
        <div className="row">
          <NumberInput id="g-gmin" size="sm" label="Cách nhau từ (giây)" min={5} max={86400} step={5} value={t.giayMin} onChange={(_e, { value }) => tl({ giayMin: secs(value) })} />
          <NumberInput id="g-gmax" size="sm" label="đến (giây)" min={5} max={86400} step={5} value={t.giayMax} onChange={(_e, { value }) => tl({ giayMax: secs(value) })} />
        </div>
        <div className="row">
          <MoneyInput id="g-tmin" size="sm" labelText="Tiền thắng ảo từ" maxDigits={13} value={t.tienMin || ''} onChange={(v) => tl({ tienMin: Number(v || 0) })} />
          <MoneyInput id="g-tmax" size="sm" labelText="đến" maxDigits={13} value={t.tienMax || ''} onChange={(v) => tl({ tienMax: Number(v || 0) })} />
        </div>
        <p className="muted note">Câu chữ và danh sách game của chữ chạy cố định trên máy chủ; ở đây chỉ bật / tắt và chỉnh nhịp.</p>
      </fieldset>
      <fieldset className="control__group" disabled={!editable}>
        <legend>Bảng xếp hạng ảo</legend>
        <Toggle id="g-ca" size="sm" labelText="Bot chơi ảo cho bảng xếp hạng slot / Mini Poker" labelA="Tắt" labelB="Bật" toggled={f.bot.choiAo.bat}
          onToggle={(v) => setF((o) => ({ ...o, bot: { choiAo: { ...o.bot.choiAo, bat: v } } }))} />
        <NumberInput id="g-cag" size="sm" label="Mỗi game một ván ảo mỗi (giây)" min={5} max={86400} step={5} value={f.bot.choiAo.giay}
          onChange={(_e, { value }) => setF((o) => ({ ...o, bot: { choiAo: { ...o.bot.choiAo, giay: secs(value) } } }))} />
      </fieldset>
      <SaveBar editable={editable} dirty={dirty} invalid={invalid} heading="Đổi luật hũ và thông báo"
        warning="Luật hũ quyết định ai được nổ hũ ở mọi game."
        reset={() => setF(globalForm(x))} body={() => ({ scope: 'global', block: f, expect: version })} />
    </section>
  );
}

const PENDING_LABEL: Record<string, string> = { 'games.control': 'Bàn', 'games.control.game': 'Bot', 'games.control.global': 'Luật hũ và thông báo' };
const pendingWhat = (p: Pending) => {
  if (p.sourceType === 'games.control.global') return PENDING_LABEL[p.sourceType];
  const id = p.sourceId ?? '';
  const name = p.sourceType === 'games.control' ? GAME_BY_ID.get(id === '5996' ? 'kts_5996' : 'ktrng_' + id)?.name : GAME_BY_ID.get(id)?.name;
  return `${PENDING_LABEL[p.sourceType ?? ''] ?? 'Điều khiển'} ${name ?? id}`;
};

export function GameControlPage() {
  const { can, me } = useAuth();
  const q = useQuery({ queryKey: ['game-control'], queryFn: () => api.get<Data>('/api/admin/games/control') });
  if (q.isLoading) return <div className="page"><BlockSkeleton lines={10} /></div>;
  if (q.error || !q.data) return <div className="page"><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const { tables, games, global, meta, pending } = q.data;
  const editable = can('games.config');
  const k = ':' + meta.version;
  return (
    <div className="page">
      <PageHeader title="Quỹ và bot"
        lead="Luật nổ hũ và chữ chạy sảnh cho mọi game, bot của từng game, quỹ của bốn bàn trực tiếp. Mọi thay đổi áp dụng ngay khi lưu, không chờ người duyệt." />
      <div className="row health">
        <span>Cấu hình CMS: {meta.source === 'default' ? <span className="muted">chưa đặt</span> : <>phiên bản <span className="num">{meta.version}</span>{meta.at ? <span className="muted">, <When v={meta.at} /></span> : null}</>}</span>
      </div>
      {pending.length ? (
        <>
          <InlineNotification kind="info" lowContrast hideCloseButton title={`${pending.length} lệnh đang chờ`}
            subtitle="Đợi lệnh đang gửi xong, hoặc duyệt / từ chối lệnh còn chờ duyệt từ trước, rồi mới tạo lệnh mới: lệnh tạo trên phiên bản cũ sẽ bị hệ A từ chối." />
          <ul className="list pending-list">
            {pending.map((p) => (
              <li key={p.id}><CommandStatus s={p.status} /><span>{pendingWhat(p)}</span><Link className="cell-link" to={'/commands/' + p.id}>Xem lệnh</Link><span className="muted"><When v={p.createdAt} /></span></li>
            ))}
          </ul>
        </>
      ) : null}
      {global ? (
        <section className="section section--flush">
          <div className="control-grid">
            <GlobalPanel key={'global' + k} x={global} version={meta.version} editable={editable} />
          </div>
        </section>
      ) : null}
      {games.length ? (
        <section className="section">
          <h2>Bot mini game</h2>
          <div className="control-grid">
            {games.map((g) => <GamePanel key={g.gid + k} g={g} version={meta.version} editable={editable} />)}
          </div>
        </section>
      ) : null}
      <section className="section">
        <h2>Bàn trực tiếp</h2>
        <p className="muted note">Khi bật điều khiển, kết quả vẫn ngẫu nhiên; chỉ khi kết quả đó bắt quỹ trả người thật nhiều hơn số dư quỹ thì hệ A quay lại.</p>
        <div className="control-grid">
          {tables.map((t) => <TablePanel key={t.gid + k} t={t} version={meta.version} editable={editable} founder={me?.role === 'founder'} />)}
        </div>
      </section>
    </div>
  );
}
